import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Calendar,
  Sparkles,
  Loader2,
  Download,
  Copy,
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  Users,
  Target,
  FileText,
  ChevronRight,
  Trophy,
  Zap,
  AlertCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { useDashboardData, useSyncedTasks, useSyncedMeetings } from '@/hooks/useSyncedData';
import { stripEmoji } from '@/lib/reportMarkdown';
import { downloadReportPdf } from '@/lib/reportPdf';
import { buildWeekData } from '@/lib/weeklyDigestData';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { useLocalization } from '@/hooks/useLocalization';

interface DigestData {
  executiveSummary: string;
  weekHighlights: Array<{ icon: string; title: string; description: string }>;
  projectUpdates: Array<{
    name: string;
    status: 'on-track' | 'at-risk' | 'delayed';
    progress: number;
    keyUpdate: string;
    nextMilestone: string;
  }>;
  tasksOverview: {
    completed: number;
    inProgress: number;
    blocked: number;
    highlight: string;
  };
  risksAndBlockers: Array<{
    severity: 'high' | 'medium' | 'low';
    description: string;
    mitigation: string;
  }>;
  teamPerformance: {
    velocity: string;
    recognition: string;
  };
  nextWeekPriorities: string[];
  actionItems: Array<{
    owner: string;
    action: string;
    due: string;
  }>;
}

// Mock week data for the digest
const mockWeekData = {
  dateRange: {
    start: format(startOfWeek(new Date()), 'yyyy-MM-dd'),
    end: format(endOfWeek(new Date()), 'yyyy-MM-dd')
  },
  projects: [
    { name: 'Enterprise Platform Migration', progress: 68, status: 'at-risk', tasksCompleted: 12, tasksTotal: 45, budget: { spent: 245000, total: 350000 } },
    { name: 'Mobile App Redesign', progress: 85, status: 'on-track', tasksCompleted: 34, tasksTotal: 40, budget: { spent: 125000, total: 150000 } },
    { name: 'Data Analytics Pipeline', progress: 45, status: 'on-track', tasksCompleted: 18, tasksTotal: 40, budget: { spent: 80000, total: 200000 } },
    { name: 'Security Compliance Update', progress: 72, status: 'delayed', tasksCompleted: 29, tasksTotal: 40, budget: { spent: 95000, total: 100000 } },
  ],
  tasks: {
    completed: 24,
    newlyCreated: 15,
    inProgress: 18,
    blocked: 3,
    overdue: 2
  },
  meetings: {
    held: 8,
    actionItemsGenerated: 23,
    decisionsRecorded: 12
  },
  emails: {
    processed: 156,
    tasksExtracted: 18,
    highPriority: 7
  },
  team: {
    activeMembers: 12,
    avgWorkload: 78,
    topPerformer: 'Sarah Chen'
  }
};

// The model supplies an emoji per highlight; render a consistent icon instead,
// chosen from the wording so gaps/risks read as warnings and wins as positives.
const HighlightIcon = ({ text }: { text: string }) => {
  const t = text.toLowerCase();
  if (/(gap|risk|delay|slip|block|overrun|issue|critical|behind)/.test(t)) {
    return <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" />;
  }
  if (/(launch|release|kickoff|start|prototype)/.test(t)) {
    return <Zap className="h-4 w-4 text-primary" aria-hidden="true" />;
  }
  return <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-hidden="true" />;
};

const WeeklyDigestView = () => {
  const { tx, t } = useLocalization();
  const { isDemoTenant } = useTenant();
  const weekStart = startOfWeek(new Date());
  const weekEnd = endOfWeek(new Date());
  const { projects: syncedProjects, hasSyncedData } = useDashboardData();
  const { data: syncedTasks } = useSyncedTasks();
  const { data: syncedMeetings } = useSyncedMeetings(weekStart.toISOString(), weekEnd.toISOString());

  const realWeekData = buildWeekData(
    syncedProjects.map((p) => ({ id: p.id, name: p.name, health: p.health, progress: p.progress, budget: p.budget, spent: p.spent })),
    syncedTasks ?? [],
    syncedMeetings ?? [],
    syncedProjects.flatMap((p) => p.team.map((t) => t.id)),
    weekStart,
    weekEnd,
  );
  // Real data wins whenever it exists; mockWeekData is an empty-state fallback for demo tenants only.
  const weekData = hasSyncedData || !isDemoTenant ? realWeekData : mockWeekData;
  const [digest, setDigest] = useState<DigestData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Load last persisted digest on mount
  useEffect(() => {
    const loadDigest = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('ai_outputs')
        .select('payload')
        .eq('source_module', 'generate-weekly-digest')
        .eq('generated_by', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !data) return;
      setDigest(data.payload as unknown as DigestData);
    };

    loadDigest();
  }, []);

  const generateDigest = async () => {
    setIsGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-weekly-digest', {
        body: { weekData: weekData },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setDigest(data as DigestData);
      toast.success(t('Weekly digest generated successfully'));
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('Failed to generate digest'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!digest) return;
    
    const text = `Weekly Digest - ${format(new Date(), 'MMM d, yyyy')}\n\n${digest.executiveSummary}\n\nHighlights:\n${digest.weekHighlights.map(h => `• ${h.title}: ${h.description}`).join('\n')}\n\nNext Week Priorities:\n${digest.nextWeekPriorities.map((p, i) => `${i + 1}. ${p}`).join('\n')}`;
    
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(t('Copied to clipboard'));
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!digest) return;
    
    const content = `# Weekly Digest - ${format(new Date(), 'MMMM d, yyyy')}\n\n## Executive Summary\n${digest.executiveSummary}\n\n## Week Highlights\n${digest.weekHighlights.map(h => `- **${h.title}**: ${h.description}`).join('\n')}\n\n## Project Updates\n${digest.projectUpdates.map(p => `### ${p.name}\n- Status: ${p.status}\n- Progress: ${p.progress}%\n- Key Update: ${p.keyUpdate}\n- Next Milestone: ${p.nextMilestone}`).join('\n\n')}\n\n## Tasks Overview\n- Completed: ${digest.tasksOverview.completed}\n- In Progress: ${digest.tasksOverview.inProgress}\n- Blocked: ${digest.tasksOverview.blocked}\n\n## Risks & Blockers\n${digest.risksAndBlockers.map(r => `- [${r.severity.toUpperCase()}] ${r.description}\n  - Mitigation: ${r.mitigation}`).join('\n')}\n\n## Next Week Priorities\n${digest.nextWeekPriorities.map((p, i) => `${i + 1}. ${p}`).join('\n')}\n\n## Action Items\n${digest.actionItems.map(a => `- [ ] ${a.action} (@${a.owner}, due: ${a.due})`).join('\n')}`;
    
    downloadReportPdf(
      {
        title: `Weekly Digest - ${format(new Date(), 'MMMM d, yyyy')}`,
        generatedAt: new Date().toISOString(),
        content: content.replace(/^# .*\n+/, ''),
      },
      `weekly-digest-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
    );
    toast.success(t('Digest downloaded as PDF'));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on-track': return 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400';
      case 'at-risk': return 'bg-amber-500/20 text-amber-600 dark:text-amber-400';
      case 'delayed': return 'bg-destructive/20 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-destructive/20 text-destructive';
      case 'medium': return 'bg-amber-500/20 text-amber-600 dark:text-amber-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="nexus-fade-in flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold text-foreground">{t('Weekly Digest')}</h2>
          </div>
          <p className="text-muted-foreground mt-1">
            
            {t('AI-generated status summary for')} {format(startOfWeek(new Date()), 'MMM d')} - {format(endOfWeek(new Date()), 'MMM d, yyyy')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {digest && (
            <>
              <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1">
                {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? t('Copied') : t('Copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1">
                <Download className="h-4 w-4" />
                
                {t('Download')}
              </Button>
            </>
          )}
          <Button onClick={generateDigest} disabled={isGenerating} className="gap-2">
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {digest ? t('Regenerate') : t('Generate Digest')}
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="nexus-fade-in" style={{ animationDelay: '0.1s' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Target className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{weekData.projects.length}</p>
                <p className="text-xs text-muted-foreground">{t('Active Projects')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="nexus-fade-in" style={{ animationDelay: '0.15s' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{weekData.tasks.completed}</p>
                <p className="text-xs text-muted-foreground">{t('Tasks Completed')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="nexus-fade-in" style={{ animationDelay: '0.2s' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{weekData.meetings.held}</p>
                <p className="text-xs text-muted-foreground">{t('Meetings Held')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="nexus-fade-in" style={{ animationDelay: '0.25s' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{weekData.team.activeMembers}</p>
                <p className="text-xs text-muted-foreground">{t('Team Members')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Generate Prompt or Digest Content */}
      {!digest && !isGenerating && (
        <Card className="nexus-fade-in">
          <CardContent className="py-12 text-center">
            <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
              <FileText className="h-10 w-10 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{t('Generate Your Weekly Digest')}</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              
              {t('AI will analyze your projects, tasks, meetings, and emails to create a comprehensive weekly status summary.')}
            </p>
            <Button onClick={generateDigest} size="lg" className="gap-2">
              <Sparkles className="h-5 w-5" />
              
              {t('Generate Weekly Digest')}
            </Button>
          </CardContent>
        </Card>
      )}

      {isGenerating && (
        <Card className="nexus-fade-in">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{t('Analyzing your week and generating digest...')}</p>
          </CardContent>
        </Card>
      )}

      {digest && (
        <div className="space-y-6">
          {/* Executive Summary */}
          <Card className="nexus-fade-in border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                
                {t('Executive Summary')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground leading-relaxed">{digest.executiveSummary}</p>
            </CardContent>
          </Card>

          {/* Week Highlights */}
          {digest.weekHighlights.length > 0 && (
            <Card className="nexus-fade-in" style={{ animationDelay: '0.1s' }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-amber-500" />
                  
                  {t('Week Highlights')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {digest.weekHighlights.map((highlight, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background border border-border">
                        <HighlightIcon text={`${highlight.title} ${highlight.description}`} />
                      </span>
                      <div>
                        <p className="font-medium text-sm">{stripEmoji(highlight.title)}</p>
                        <p className="text-xs text-muted-foreground">{stripEmoji(highlight.description)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Project Updates */}
          <Card className="nexus-fade-in" style={{ animationDelay: '0.15s' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                
                {t('Project Updates')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {digest.projectUpdates.map((project, idx) => (
                <div key={idx} className="p-3 rounded-lg border bg-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">{project.name}</span>
                    <Badge className={cn("text-xs", getStatusColor(project.status))}>
                      {tx(project.status)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Progress value={project.progress} className="flex-1 h-2" />
                    <span className="text-xs font-medium w-10">{project.progress}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">
                    <span className="font-medium text-foreground">{t('Update:')}</span> {project.keyUpdate}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{t('Next:')}</span> {project.nextMilestone}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tasks Overview */}
            <Card className="nexus-fade-in" style={{ animationDelay: '0.2s' }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  
                  {t('Tasks Overview')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 rounded-lg bg-emerald-500/10">
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{digest.tasksOverview.completed}</p>
                    <p className="text-xs text-muted-foreground">{t('Completed')}</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-primary/10">
                    <p className="text-2xl font-bold text-primary">{digest.tasksOverview.inProgress}</p>
                    <p className="text-xs text-muted-foreground">{t('In Progress')}</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-destructive/10">
                    <p className="text-2xl font-bold text-destructive">{digest.tasksOverview.blocked}</p>
                    <p className="text-xs text-muted-foreground">{t('Blocked')}</p>
                  </div>
                </div>
                {digest.tasksOverview.highlight && (
                  <div className="p-3 rounded-lg bg-secondary/30 text-sm flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <span>{digest.tasksOverview.highlight}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Team Performance */}
            <Card className="nexus-fade-in" style={{ animationDelay: '0.25s' }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  
                  {t('Team Performance')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-xs text-muted-foreground mb-1">{t('Velocity')}</p>
                  <p className="text-sm">{digest.teamPerformance.velocity}</p>
                </div>
                {digest.teamPerformance.recognition && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy className="h-4 w-4 text-amber-500" />
                      <p className="text-xs font-medium text-amber-600 dark:text-amber-400">{t('Recognition')}</p>
                    </div>
                    <p className="text-sm">{digest.teamPerformance.recognition}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Risks & Blockers */}
          {digest.risksAndBlockers.length > 0 && (
            <Card className="nexus-fade-in" style={{ animationDelay: '0.3s' }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  
                  {t('Risks & Blockers')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {digest.risksAndBlockers.map((risk, idx) => (
                  <div key={idx} className="p-3 rounded-lg border">
                    <div className="flex items-start gap-2 mb-2">
                      <Badge className={cn("text-xs", getSeverityColor(risk.severity))}>
                        {tx(risk.severity)}
                      </Badge>
                      <p className="text-sm flex-1">{tx(risk.description)}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">{t('Mitigation:')}</span> {risk.mitigation}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Next Week Priorities & Action Items */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="nexus-fade-in" style={{ animationDelay: '0.35s' }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  
                  {t('Next Week Priorities')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {digest.nextWeekPriorities.map((priority, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30">
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-primary">{idx + 1}</span>
                      </div>
                      <span className="text-sm">{tx(priority)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="nexus-fade-in" style={{ animationDelay: '0.4s' }}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  
                  {t('Action Items')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {digest.actionItems.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 rounded-lg border">
                      <input type="checkbox" className="mt-1 rounded" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">{item.action}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span className="font-medium">@{item.owner}</span>
                          <span>•</span>
                          <span>{t('Due:')} {item.due}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyDigestView;
